
import { defineComponent, computed, PropType } from 'vue';
import {
  formatPercentage,
  formatCurrency,
  formatDecimal,
  formatInteger,
  formatCompact,
} from './formatters';
import type { MetricType, MetricLayout } from './displayTypes';
import SpmHelpIcon from './SpmHelpIcon.vue';

export default defineComponent({
  name: 'SpmMetricDisplay',

  components: { SpmHelpIcon },

  props: {
    /**
     * Label for the metric
     */
    label: {
      type: String,
      required: true,
    },

    /**
     * Help text shown in a tooltip next to the label
     */
    help: {
      type: String,
      default: '',
    },

    /**
     * Value to display
     */
    value: {
      type: [Number, String] as PropType<number | string | null | undefined>,
      default: null,
    },

    /**
     * Type of metric formatting
     */
    type: {
      type: String as PropType<MetricType>,
      default: 'raw',
    },

    /**
     * Layout direction
     */
    layout: {
      type: String as PropType<MetricLayout>,
      default: 'horizontal',
    },

    /**
     * Currency code (for currency type)
     */
    currency: {
      type: String,
      default: 'EUR',
    },

    /**
     * Number of decimal places (for decimal type)
     */
    decimals: {
      type: Number,
      default: 2,
    },

    /**
     * Whether percentage value is already decimal (0-1) or percentage (0-100)
     */
    isDecimal: {
      type: Boolean,
      default: true,
    },

    /**
     * Placeholder for empty values
     */
    placeholder: {
      type: String,
      default: '-',
    },
  },

  setup(props) {
    const formattedValue = computed(() => {
      if (props.value === null || props.value === undefined) {
        return props.placeholder;
      }

      if (typeof props.value === 'string') {
        return props.value;
      }

      switch (props.type) {
        case 'percentage':
          return formatPercentage(props.value, props.isDecimal, props.decimals);
        case 'currency':
          return formatCurrency(props.value, props.currency);
        case 'decimal':
          return formatDecimal(props.value, props.decimals);
        case 'integer':
          return formatInteger(props.value);
        case 'compact':
          return formatCompact(props.value);
        case 'raw':
        default:
          return String(props.value);
      }
    });

    const layoutClass = computed(() => `spm-metric-display--${props.layout}`);

    return {
      formattedValue,
      layoutClass,
    };
  },
});
